import config from "@/config"
import { isComingSoon } from "@/core/launch"

/**
 * Builds an absolute, self-referential canonical URL for a given path.
 *
 * Centralised here (rather than inlined per-route) so that locale-prefixed
 * routing (T8, out of scope for this task) only needs to change this one
 * function: pass a `localePrefix` like `"/zh-hans"` and every canonical,
 * sitemap and JSON-LD URL in the app picks it up. Never point a localized
 * page's canonical at a different locale's URL - each must be
 * self-referential (see docs/research/marketing-sources/seo-and-content.md).
 */
export function buildCanonicalUrl(pathname: string, localePrefix = ""): string {
  const normalizedPrefix = localePrefix ? `/${localePrefix.replace(/^\/|\/$/g, "")}` : ""
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`
  // The prefixed home page is "/zh", without a slash: the site serves drop-trailing-slash (wrangler.json).
  const path = !normalizedPrefix
    ? normalizedPath
    : normalizedPath === "/"
      ? normalizedPrefix
      : `${normalizedPrefix}${normalizedPath}`
  return new URL(path, config.core.websiteUrl).href
}

/** Product name shown in nav, footer and metadata. The only source is config (PRD R7.7). */
export function getSiteName(): string {
  return config.core.projectName
}

export function getSiteTagline(): string {
  return config.core.tagline ?? ""
}

/** True when the request is for the canonical marketing host (config `websiteUrl`). */
export function isMarketingHost(request: Request): boolean {
  return new URL(request.url).host === new URL(config.core.websiteUrl).host
}

/** app. and api. hosts must stay out of search results: noindex header, never `Disallow: /`. */
export function withNoindexOutsideMarketingHost(request: Request, response: Response): Response {
  if (isMarketingHost(request) && !isComingSoon()) return response
  const headers = new Headers(response.headers)
  headers.set("X-Robots-Tag", "noindex")
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  })
}
